import React, { useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { OPTION_LEADERBOARD_EVENTS_DATES } from 'constants/dateTime';
import { toUTC, timeNow } from 'helpers/day';
import { useTheme } from 'hooks';
import Box from 'UIKit/Box';

import classes from './LiveStatus.module.scss';
import { MARKETS_CARD_TYPES } from '../CardsArea.constants';
import { TagBox } from '../TagBox';
import { MarketCardValueTypes } from '../types';

/**
 * @description Function to check when to show live status for each offer card .
 */
const isBetweenValidTimeSlot = (offerType: MarketCardValueTypes) => {
  const currentTime = toUTC(timeNow());
  if (offerType === MARKETS_CARD_TYPES.SCALPER_CARNIVAL_CARD) {
    /**
     * Text only needs to be shown between 12:30 PM UTC to 4:30 AM UTC
     * In IST it will be 6 PM to 10 AM
     */
    const startTime = toUTC(timeNow())
      .set('hours', 12)
      .set('minutes', 30)
      .set('seconds', 0);

    const endTime = toUTC(timeNow())
      .add(1, 'day')
      .set('hours', 4)
      .set('minutes', 30)
      .set('seconds', 0);

    return currentTime.isAfter(startTime) && currentTime.isBefore(endTime);
  }

  if (offerType === MARKETS_CARD_TYPES.FUTURES_SCALPER_CARNIVAL_CARD) {
    /**
     * Text only needs to be shown between 00:00 AM UTC to 23:59 PM UTC
     */
    const startTime = toUTC(timeNow())
      .set('hours', 0)
      .set('minutes', 0)
      .set('seconds', 0);

    const endTime = toUTC(timeNow())
      .set('hours', 23)
      .set('minutes', 59)
      .set('seconds', 0);

    return currentTime.isAfter(startTime) && currentTime.isBefore(endTime);
  }

  return (
    currentTime.isAfter(OPTION_LEADERBOARD_EVENTS_DATES.EVENT_START_DATE) &&
    currentTime.isBefore(OPTION_LEADERBOARD_EVENTS_DATES.EVENT_END_DATE)
  );
};

interface LiveStatusProps {
  offerType: MarketCardValueTypes;
}

const LiveStatus = ({ offerType }: LiveStatusProps) => {
  const { t } = useTranslation(['common']);
  const { activeTheme } = useTheme();

  const [isVisible, setVisible] = useState(() => isBetweenValidTimeSlot(offerType));

  useEffect(() => {
    // Just checking after every minute, if text needs to be shown.
    const timerId = setInterval(() => {
      setVisible(isBetweenValidTimeSlot(offerType));
    }, 1000 * 60);
    return () => {
      clearInterval(timerId);
    };
  }, [offerType]);

  if (!isVisible) {
    return null;
  }

  return (
    <TagBox>
      <Box as="span" className={cn(classes.text, classes[activeTheme])}>
        {t('common:liveNow')}
      </Box>
      <Box as="span" className={classes.dot} />
    </TagBox>
  );
};

export { LiveStatus };
